import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getPreviewUrl } from '../../../../../services/attachmentService';
import { inferFileType } from '../../../utils/fileOverrides';

/**
 * Full-screen preview for a mission file-upload — same media types as
 * the attachments viewer (image / pdf / video / audio).
 */
export const FileUploadPreview = ({ missionId, file, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const videoRef = useRef(null);

  useEffect(() => {
    let live = true;
    getPreviewUrl(missionId, file.attachmentId)
      .then((next) => {
        if (live) setData(next);
      })
      .catch((err) => {
        if (live) setError(err.message || 'Preview failed');
      });
    return () => {
      live = false;
    };
  }, [file.attachmentId, missionId]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const fileType = inferFileType(
    file.mimeType || data?.mimeType,
    file.filename,
    file.fileType || data?.fileType
  );

  useEffect(() => {
    if (fileType !== 'video' || !data?.previewUrl) return undefined;
    const player = videoRef.current;
    if (!player) return undefined;
    const play = () => player.play().catch(() => {});
    play();
    player.addEventListener('canplay', play);
    return () => player.removeEventListener('canplay', play);
  }, [data?.previewUrl, fileType]);

  return createPortal(
    <div
      className="file-upload-preview"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="file-upload-preview__bar"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="file-upload-preview__name">{file.filename}</span>
        <button
          type="button"
          className="file-upload-preview__close"
          onClick={onClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
      <div
        className="file-upload-preview__body"
        onClick={(e) => e.stopPropagation()}
      >
        {error ? (
          <p className="file-upload-preview__status">{error}</p>
        ) : !data ? (
          <p className="file-upload-preview__status">Loading preview…</p>
        ) : fileType === 'image' ? (
          <img
            className="file-upload-preview__image"
            src={data.previewUrl}
            alt={file.filename}
          />
        ) : fileType === 'pdf' ? (
          <iframe
            className="file-upload-preview__frame"
            src={data.previewUrl}
            title={file.filename}
          />
        ) : fileType === 'video' ? (
          <video
            ref={videoRef}
            className="file-upload-preview__media"
            src={data.previewUrl}
            poster={file.thumbDataUrl}
            controls
            autoPlay
            playsInline
          />
        ) : fileType === 'audio' ? (
          <audio className="file-upload-preview__media" src={data.previewUrl} controls />
        ) : (
          <p className="file-upload-preview__status">
            Preview is not available for this file type
          </p>
        )}
      </div>
    </div>,
    document.body
  );
};
